import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatDate } from '../../../../../helpers/date'
import { theme } from '../../../../../theme/theme'
import { cn } from '../../../../../helpers/utils'
import { ITicket } from '../../../../../interfaces/models/ticket.interfaces'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { societyQueries } from '../../../../../api/societies'
import {
  SocietyAvatar,
  TicketAvatar,
} from '../../../../../components/common/Avatar'
import { useAuth } from '../../../../../hooks/useAuth'
import { TicketVariantBadge } from '../../../../../components/ticket/TicketVariantBadge'
import { SPACE_TRANSLATION_KEY } from '../../../../../helpers/ticketSpace'

const SocietyAuthor = ({ ticket }: { ticket: ITicket }): React.ReactNode => {
  const { data: society } = useQuery(
    societyQueries.getSociety(ticket.societyId)
  )

  return (
    <div className="flex gap-2 items-center">
      <SocietyAvatar society={society} />
      <div>
        <p style={theme.textVariants.base}>{society?.name}</p>
        <p style={theme.textVariants.base}>
          {formatDate(ticket.createdAt, 'long')}
        </p>
      </div>
    </div>
  )
}
const UserAuthor = ({ ticket }: { ticket: ITicket }): React.ReactNode => {
  const { userId } = useAuth()
  const { data: societyUsers } = useQuery(
    societyQueries.getSocietyIdUsers(ticket.societyId)
  )
  const author = societyUsers?.find((user) => user._id === userId)

  return (
    <div className="flex gap-2 items-center">
      {author && <TicketAvatar user={author} url={author?.avatarUrl} />}
      <div>
        <p style={theme.textVariants.base}>{author?.fullName}</p>
        <p style={theme.textVariants.base}>{author?.unitString}</p>
        <p style={theme.textVariants.base}>
          {formatDate(ticket.createdAt, 'long')}
        </p>
      </div>
    </div>
  )
}
const Author = ({ ticket }: { ticket: ITicket }): React.ReactNode => {
  const { translate } = useAppTranslation()

  return (
    <div className="mb-4">
      <p style={theme.textVariants.baseBold}>
        {translate('ticketBoardSidebar.createdByLabel')}
      </p>
      {ticket.author === 'society' && <SocietyAuthor ticket={ticket} />}
      {ticket.author === 'me' && <UserAuthor ticket={ticket} />}
    </div>
  )
}

export const TicketInformation = ({
  ticket,
  className,
}: {
  ticket: ITicket
  className: string
}): React.ReactNode => {
  const { translate } = useAppTranslation()

  return (
    <section className={cn(className)}>
      <Author ticket={ticket} />
      {ticket.title && (
        <div className="mb-4">
          <p style={theme.textVariants.baseBold}>
            {translate('ticketSidebar.titleLabel')}
          </p>
          <p style={theme.textVariants.base}>{ticket.title}</p>
        </div>
      )}

      {ticket.description && (
        <div className="mb-4">
          <p style={theme.textVariants.baseBold}>
            {translate('ticketSidebar.descriptionLabel')}
          </p>
          <p
            style={theme.textVariants.base}
            className="max-h-48 overflow-y-auto whitespace-pre-line"
          >
            {ticket.description}
          </p>
        </div>
      )}

      <div className="mb-4">
        <p style={theme.textVariants.baseBold}>
          {translate('ticketSidebar.variantLabel')}
        </p>
        <TicketVariantBadge variant={ticket.variant} />
      </div>

      {ticket.variant === 'report' && ticket.space && (
        <div className="mb-4">
          <p style={theme.textVariants.baseBold}>
            {translate('ticketSidebar.spaceLabel')}
          </p>
          <p style={theme.textVariants.base}>
            {translate(SPACE_TRANSLATION_KEY[ticket.space])}
          </p>
        </div>
      )}
    </section>
  )
}
